import React, { useEffect, useRef, useState } from "react";

import {
  isNullOrUndefined,
  isValidNonnegativeNumber,
  useGetDairyFeedAdditives,
  useGetFootprintingDairyFeedAdditive,
  useGetFootprintingDairyPrimaryDataById,
  useMutateFootprintingDairyFeedAdditive,
  useMutateFootprintingDairyPrimaryData,
  useTranslation,
} from "utils";

import {
  DAIRY_PRACTICE_SLUG,
  ENUM_DAIRY_FEED_ADDITIVE_UNITS,
  ENUM_FARM_FOOTPRINTING_DAIRY_FEED_ADDITIVES,
  FARM_FOOTPRINTING_DAIRY_FEED_ADDITIVES,
  UploadableFeedAdditive,
} from "models";

import { Button, Card, Flex, Form, FormInstance, Icon, IconButton, InputNumberWithUnitSelector, KlimRadioGroup, Option, PageSpin, Select } from "components";

interface FarmGeneralProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
}

const feedAdditivePlaceholder: UploadableFeedAdditive = {
  id: undefined,
  feedAdditiveId: "",
  amount: null,
  amountUnit: null,
};

export const FeedAdditiveForm = ({ primaryDataId, readOnly, farmId, year }: FarmGeneralProps) => {
  const { t } = useTranslation(["footprintingDairy", "dairy"]);
  const formRef = useRef<FormInstance>(null);

  const [feedAdditiveActive, setFeedAdditiveActive] = useState<ENUM_FARM_FOOTPRINTING_DAIRY_FEED_ADDITIVES | null>(null);
  const [appliedFeedAdditives, setAppliedFeedAdditives] = useState<UploadableFeedAdditive[]>([]);

  const { data: dairyPrimaryDataQuery, isLoading } = useGetFootprintingDairyPrimaryDataById(primaryDataId ?? "");
  const { data: feedAdditivesQuery } = useGetFootprintingDairyFeedAdditive(primaryDataId);
  const { data: dairyFeedAdditivesQuery } = useGetDairyFeedAdditives();

  const selectGroupName = "footprinting-dairy-feed-additive-select";

  const { mutate: mutateDairyFeedAdditives } = useMutateFootprintingDairyFeedAdditive(
    farmId,
    year,
    {
      footprintingFarmPrimaryDataId: primaryDataId ?? "",
      selectedFeedAdditive: appliedFeedAdditives.filter((f) => !!f.feedAdditiveId),
    },
    primaryDataId
  );

  const { mutate: mutateDairyPrimaryData } = useMutateFootprintingDairyPrimaryData(
    farmId,
    year,
    {
      farmId,
      year,
      feedAdditiveActive,
    },
    primaryDataId
  );

  // Save on unmount
  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateDairyPrimaryData();
        mutateDairyFeedAdditives();
      }
    },
    [isLoading, mutateDairyPrimaryData, mutateDairyFeedAdditives, readOnly]
  );

  // Load feed additives data
  useEffect(() => {
    if (feedAdditivesQuery) {
      setAppliedFeedAdditives(
        feedAdditivesQuery.length
          ? feedAdditivesQuery.map((f) => ({
              id: f.id,
              feedAdditiveId: f.feedAdditiveId ?? "",
              amount: f.amount ?? null,
              amountUnit: f.amountUnit ?? null,
            }))
          : [{ ...feedAdditivePlaceholder }]
      );
    }
  }, [feedAdditivesQuery]);

  // Load primary data
  useEffect(() => {
    if (dairyPrimaryDataQuery) {
      setFeedAdditiveActive(dairyPrimaryDataQuery.feedAdditiveActive ?? null);

      formRef.current?.setFieldsValue({
        feedAdditiveActive: dairyPrimaryDataQuery.feedAdditiveActive ?? null,
      });

      if (dairyPrimaryDataQuery.feedAdditiveActive !== null && dairyPrimaryDataQuery.feedAdditiveActive !== undefined) {
        formRef.current?.validateFields();
      }
    }
  }, [dairyPrimaryDataQuery]);

  // This ensures that all form fields rerender when the non-last additive
  // is removed (i.e. they appear to slide up)
  useEffect(() => {
    if (appliedFeedAdditives.length > 0) {
      formRef.current?.resetFields();
    }
  }, [appliedFeedAdditives.length]);

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <div>
      <h1 className="center margin-bottom-200 padding-left-400 padding-right-400 margin-left-100 margin-right-100">{t("dairyFeedAdditivesForm.title")}</h1>
      <Card dataTestId="footprinting-dairy-feed-additives-form" className="margin-bottom-100 padding-100">
        <Flex align="center" gap={16} justify="space-between" className="padding-100">
          <span style={{ maxWidth: "60%" }}>
            {t("dairyFeedAdditivesForm.feedAdditivesQuestion")}
            <span className="required-asterisk">*</span>
          </span>
          <KlimRadioGroup
            required
            name="feedAdditiveActive"
            dataTestId="footprinting-dairy-feed-additives-radio"
            disabled={readOnly}
            vertical={false}
            type="tertiary"
            size="large"
            onChange={({ target }) => {
              setFeedAdditiveActive(target.value);
              if (target.value === FARM_FOOTPRINTING_DAIRY_FEED_ADDITIVES.YES && appliedFeedAdditives.length === 0) {
                setAppliedFeedAdditives([{ ...feedAdditivePlaceholder }]);
              }
            }}
            value={feedAdditiveActive}
            defaultValue={feedAdditiveActive}
            optionType="button"
            options={[FARM_FOOTPRINTING_DAIRY_FEED_ADDITIVES.YES, FARM_FOOTPRINTING_DAIRY_FEED_ADDITIVES.NO].map((feedAdditiveActive) => ({
              label: t(`dairyFeedAdditivesForm.feedAdditiveOptions.${feedAdditiveActive}`),
              value: feedAdditiveActive,
            }))}
          />
        </Flex>
        <Form ref={formRef}>
          {feedAdditiveActive === FARM_FOOTPRINTING_DAIRY_FEED_ADDITIVES.YES &&
            appliedFeedAdditives.map((additive, index) => {
              const selectGroupWithIndex = `${selectGroupName}-${index}`;
              const updateItem = (item: UploadableFeedAdditive) => {
                const next = [...appliedFeedAdditives];
                next[index] = { ...item };
                setAppliedFeedAdditives(next);
              };

              return (
                <Form.Item key={index} className="padding-left-100 padding-right-100 padding-bottom-0 padding-top-75">
                  <Flex className="margin-bottom-100" justify="space-between" align="center" vertical={false}>
                    <h5 style={{ flex: 1 }}>{t("dairyFeedAdditivesForm.additiveNumber", { count: index + 1 })}</h5>
                    <IconButton
                      type="tertiary"
                      disabled={readOnly}
                      dataTestId={`delete-feed-additive-${index}`}
                      size="small"
                      iconName="tabler/trash"
                      onClick={() => setAppliedFeedAdditives([...appliedFeedAdditives].filter((_, i) => i !== index))}
                    />
                  </Flex>
                  {/* @ts-expect-error need to convert to tsx */}
                  <Select
                    disabled={readOnly}
                    className="margin-bottom-100"
                    name={selectGroupWithIndex}
                    dataTestId={selectGroupWithIndex}
                    label={t("dairyFeedAdditivesForm.question.feedAdditiveTypeQuestionInput")}
                    rules={[{ required: true, message: false }]}
                    initialValue={dairyFeedAdditivesQuery?.find((f) => f.id === additive.feedAdditiveId)?.name || ""}
                    value={dairyFeedAdditivesQuery?.find((f) => f.id === additive.feedAdditiveId)?.name || ""}
                    onChange={(v) => {
                      const matchingPractice = dairyFeedAdditivesQuery?.find((f) => f.name === v);
                      updateItem({
                        ...additive,
                        feedAdditiveId: matchingPractice ? matchingPractice.id : "",
                      });
                    }}
                    dropdownStyle={{ zIndex: 10010 }} // The modal presenting us has a z-index of 10k
                  >
                    {(dairyFeedAdditivesQuery ?? []).map((dairyFeedAdditive) => {
                      // Check if this additive ID is already used in another item
                      const isAlreadyUsed = appliedFeedAdditives.some(
                        (a) => a.feedAdditiveId === dairyFeedAdditive.id && a.feedAdditiveId !== additive.feedAdditiveId
                      );
                      return (
                        <Option
                          disabled={readOnly || isAlreadyUsed}
                          key={`${selectGroupWithIndex}-${dairyFeedAdditive.id}`}
                          data-testid={`${selectGroupWithIndex}-${dairyFeedAdditive.id}`}
                          value={dairyFeedAdditive.name}
                        >
                          {t(`${DAIRY_PRACTICE_SLUG.FEED_ADDITIVES}.${dairyFeedAdditive.translationSlug}`, { ns: "dairy" }) || dairyFeedAdditive.name}
                        </Option>
                      );
                    })}
                  </Select>

                  <InputNumberWithUnitSelector
                    key={`amount-${index}-${additive.feedAdditiveId}`}
                    className="floating-label margin-bottom-200"
                    disabled={readOnly}
                    dataTestId={`footprinting-dairy-feed-additive-amount-input-${index}`}
                    name={`amountApplied-${index}`}
                    label={t("dairyFeedAdditivesForm.question.amountAppliedQuestionInput")}
                    unitLabel={t("unit", { ns: "footprinting" })}
                    units={["kilograms", "tonnes", "cubic_meters", "liters"]}
                    value={additive.amount}
                    initialValue={additive.amount}
                    unitValue={additive.amountUnit}
                    onChange={(v, unit) => updateItem({ ...additive, amount: v, amountUnit: unit as ENUM_DAIRY_FEED_ADDITIVE_UNITS })}
                    validateFirst
                    rules={[
                      {
                        required: true,
                        validator: (_: unknown, v: number) => {
                          if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                            return Promise.reject(new Error(t("dairyFeedAdditivesForm.error.amountAppliedError")));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  />
                </Form.Item>
              );
            })}
          {feedAdditiveActive === FARM_FOOTPRINTING_DAIRY_FEED_ADDITIVES.YES && (
            <Button
              type="text"
              dataTestId="add-feed-additive-button"
              disabled={readOnly}
              onClick={() => {
                setAppliedFeedAdditives([...appliedFeedAdditives, { ...feedAdditivePlaceholder }]);
              }}
            >
              <Icon iconName="klim/plus" className="icon inline" /> {t("dairyFeedAdditivesForm.add")}
            </Button>
          )}
        </Form>
      </Card>
    </div>
  );
};
